import type {EagColumnState} from '../types/EagColumnState';

/**
 * Returns the deduplicated list of column keys that should be sent to the server
 * for a data query.
 *
 * Visible column keys come from columnStates (hidden !== true).
 * predefinedColumns have their DATA loaded regardless of visibility — they
 * carry technical identifiers (e.g. 'warehouseId') required by the API and by
 * action callbacks. This affects only the fetch: a hidden predefined column
 * stays hidden in the grid (rendering is driven by columnStates, not by this
 * list). A predefined key that is not part of the grid
 * definition at all (columnStates are seeded from the full definition, hidden
 * columns included) is dropped: the BE has no column to serve cell data from,
 * and sending an unknown key risks failing the whole data query on grid
 * configurations that lack the column.
 *
 * Pure utility — no React, no store access. Pass store.getState().columnStates
 * at call time to avoid stale-closure bugs.
 */
export const getVisibleColumnKeys = (
  columnStates: EagColumnState[],
  predefinedColumns: readonly string[]
): string[] => {
  const visibleKeys: string[] = [];
  const definitionKeys = new Set<string>();
  for (const cs of columnStates) {
    definitionKeys.add(cs.key);
    if (!cs.hidden) {
      visibleKeys.push(cs.key);
    }
  }
  const knownPredefined = predefinedColumns.filter((key) => definitionKeys.has(key));
  return [...new Set([...visibleKeys, ...knownPredefined])];
};
